import Conviva from '@convivainc/conviva-js-coresdk';
import { ClientType, ColorSpace } from '@mlbtv-clients/services';
import { WIRED, WIRELESS } from 'analytics/conviva/constants';
import { releaseConvivaAnalyticsSingleton } from 'analytics/conviva/hooks/useConviva/convivaAnalyticsSingleton';
import { EASTERN_TZ } from 'constants/date';
import { MODEL_YEAR_UNKNOWN } from 'constants/platforms';
import { ROUTES } from 'constants/screens';
import { ModelTier } from 'hooks/useDeviceModelTier/types';
import { getModelTier } from 'hooks/useDeviceModelTier/utils';
import { NavigateFunction } from 'react-router-dom';
import { EPGErrors } from 'services/epg/constants';
import fallback from 'store/config/fallback.json';
import { application, systeminfo, tvinfo } from 'tizen-common-web';
import {
  CaptionColor,
  CaptionEdge,
  CaptionFontSize,
  CaptionFontStyle,
  CaptionInfoKey,
  CaptionOpacity,
  CaptionStyle,
  TvInfoMenuKey,
  TvInfoMenuValue,
} from './platformTypes';

const { EPG_SERVICE_ERROR } = EPGErrors;

interface DeviceMetadata {
  deviceBrand: string;
  deviceManufacturer: string;
  deviceModel: string;
  operatingSystemName: string;
}

const {
  Constants: { DeviceCategory, DeviceMetadata, DeviceType },
} = Conviva;

const {
  BRAND,
  CATEGORY,
  MANUFACTURER,
  MODEL,
  OS_NAME,
  SCREEN_RESOLUTION_HEIGHT,
  SCREEN_RESOLUTION_WIDTH,
  TYPE,
} = DeviceMetadata;

export const getClientType = async () => ClientType.SAMSUNG;

export const getDeviceOSVersion = async (defaultOS: string) => {
  try {
    return window.webapis.productinfo.getFirmware();
  } catch (e) {
    console.log('error retrieving os version: ', e);
  }
  return defaultOS;
};

export const getDeviceAdParams = (adParams: Record<string, string>, _: unknown) => {
  const { adinfo } = window.webapis;
  return { ...adParams, is_lat: adinfo.isLATEnabled(), rdid: adinfo.getTIFA() };
};

export const getDeviceCaptionStyle = () => {
  const bgColorKey = tvinfo.getCaptionValue(
    CaptionInfoKey.CAPTION_BG_COLOR_KEY,
  ) as keyof typeof CaptionColor;
  const bgColor = bgColorKey === 'CAPTION_COLOR_DEFAULT' ? 'CAPTION_COLOR_BLACK' : bgColorKey;
  const bgOpacity = tvinfo.getCaptionValue(
    CaptionInfoKey.CAPTION_BG_OPACITY_KEY,
  ) as keyof typeof CaptionOpacity;
  const color = tvinfo.getCaptionValue(
    CaptionInfoKey.CAPTION_FONT_COLOR_KEY,
  ) as keyof typeof CaptionColor;
  const opacity = tvinfo.getCaptionValue(
    CaptionInfoKey.CAPTION_FONT_OPACITY_KEY,
  ) as keyof typeof CaptionOpacity;
  const edgeColor = tvinfo.getCaptionValue(
    CaptionInfoKey.CAPTION_EDGE_COLOR_KEY,
  ) as keyof typeof CaptionColor;
  const edgeType = tvinfo.getCaptionValue(
    CaptionInfoKey.CAPTION_EDGE_TYPE_KEY,
  ) as keyof typeof CaptionEdge;
  const fontSize = tvinfo.getCaptionValue(
    CaptionInfoKey.CAPTION_FONT_SIZE_KEY,
  ) as keyof typeof CaptionFontSize;
  const fontStyle = tvinfo.getCaptionValue(
    CaptionInfoKey.CAPTION_FONT_STYLE_KEY,
  ) as keyof typeof CaptionFontStyle;
  const style = tvinfo.getCaptionValue(
    CaptionInfoKey.CAPTION_STYLE_KEY as any,
  ) as keyof typeof CaptionStyle;
  const windowColor = tvinfo.getCaptionValue(
    CaptionInfoKey.CAPTION_WINDOW_COLOR_KEY,
  ) as keyof typeof CaptionColor;
  const windowOpacityKey = tvinfo.getCaptionValue(
    CaptionInfoKey.CAPTION_WINDOW_OPACITY_KEY,
  ) as keyof typeof CaptionOpacity;
  const windowOpacity =
    windowOpacityKey === 'CAPTION_OPACITY_DEFAULT'
      ? 'CAPTION_OPACITY_TRANSPARENT'
      : windowOpacityKey;

  return `
    ::cue {
      background-color: rgba(${CaptionColor[bgColor as keyof typeof CaptionColor]},${CaptionOpacity[bgOpacity]});
      color: rgba(${CaptionColor[color]},${CaptionOpacity[opacity]});
      font-family: ${CaptionFontStyle[fontStyle]};
      font-size: ${CaptionFontSize[fontSize]};
      font-style: ${style === 'CAPTION_STYLE_ITALIC' ? 'italic' : 'normal'};
      font-weight: ${style === 'CAPTION_STYLE_BOLD' ? 'bold' : 'normal'};
      line-height: 1.15;
      text-shadow: ${CaptionEdge[edgeType].replaceAll('color', CaptionColor[edgeColor])};
    }

    video::-webkit-media-text-track-display {
      background-color: rgba(${CaptionColor[windowColor]},${CaptionOpacity[windowOpacity as keyof typeof CaptionOpacity]});
      line-height: 1;
    }
  `;
};

export const getDeviceCountryCode = async () => {
  const { productinfo } = window.webapis;
  return productinfo.getSystemConfig(productinfo.ProductInfoConfigKey.CONFIG_KEY_SERVICE_COUNTRY);
};

export const getDeviceConnectionType = async (): Promise<string> => {
  return new Promise((resolve) => {
    try {
      systeminfo.getPropertyValue('ETHERNET_NETWORK', () => {
        resolve(WIRED);
      });
      systeminfo.getPropertyValue('WIFI_NETWORK', () => {
        resolve(WIRELESS);
      });
    } catch (error) {
      resolve('UNKNOWN');
    }
  });
};

export const getDeviceExitApp = () => {
  releaseConvivaAnalyticsSingleton();
  try {
    const app = application.getCurrentApplication();
    app.exit();
  } catch (e) {
    console.error(e);
  }
};

export const getDeviceExitConfirmation = (navigate?: NavigateFunction) => {
  if (navigate) navigate(ROUTES.EXIT);
};

export const getDeviceId = async (deviceId: string) => {
  try {
    return await window.webapis.adinfo.getTIFA();
  } catch (e) {
    return deviceId;
  }
};

export const getDeviceIdType = () => 'tifa';

export const getDeviceKeyboardIsOpen = async () => false;

export const getDeviceMetadata = async (deviceMetadata: DeviceMetadata) => {
  const { deviceBrand, deviceManufacturer } = deviceMetadata;

  return {
    [BRAND]: deviceBrand,
    [CATEGORY]: DeviceCategory.SAMSUNG_TV,
    [MANUFACTURER]: deviceManufacturer,
    [MODEL]: systeminfo.getCapability('http://tizen.org/system/model_name'),
    [OS_NAME]: systeminfo.getCapability('http://tizen.org/system/platform.name'),
    [SCREEN_RESOLUTION_HEIGHT]: systeminfo.getCapability('http://tizen.org/feature/screen.height'),
    [SCREEN_RESOLUTION_WIDTH]: systeminfo.getCapability('http://tizen.org/feature/screen.width'),
    [TYPE]: DeviceType.SMARTTV,
  };
};

export const getDeviceModelTier = async (modelTiers: Record<string, ModelTier>) => {
  let modelNumber = 'UNKNOWN';
  try {
    modelNumber = await getDeviceModel('UNKNOWN');
  } catch (error) {
    // Fall back to UNKNOWN set above
  }

  const parsedModelNumber = parseModelNumber(modelNumber);
  const modelTier = getModelTier(modelTiers, parsedModelNumber);
  return modelTier;
};

export const getDeviceModelYear = async () => {
  let year = MODEL_YEAR_UNKNOWN;

  try {
    const modelCode = window.webapis.productinfo.getModelCode();
    const yearCode = modelCode.slice(0, 2);
    year = `20${yearCode}`;
  } catch (error) {
    // Fall back to the current year set above
  }

  return year;
};

export const getDevicePlaybackCapabilities = async () => {
  try {
    return {
      colorSpace: [webapis.avinfo.isHdrTvSupport() ? ColorSpace.HDR : ColorSpace.SDR],
    };
  } catch (e) {
    console.warn(`Failed to retrieve webapis.avinfo.isHdrTvSupport()`, e);
    return { colorSpace: [ColorSpace.SDR] };
  }
};

export const getDeviceHasSpinner = async (
  hasSpinner: typeof fallback.featureFlags.animations.hasSpinner,
) => !hasSpinner.hasAllModelsDisabled;

const timeZoneMap = new Map<string, string>([
  ['Etc/GMT+0', 'UTC'],
  ['Etc/GMT+1', 'Atlantic/Azores'],
  ['Etc/GMT+2', 'America/Noronha'],
  ['Etc/GMT+3', 'America/Argentina/Buenos_Aires'],
  ['Etc/GMT+4', 'America/Barbados'],
  ['Etc/GMT+5', 'America/Bogota'],
  ['Etc/GMT+6', 'America/Guatemala'],
  ['Etc/GMT+7', 'America/Phoenix'],
  ['Etc/GMT+8', 'America/Los_Angeles'],
  ['Etc/GMT+9', 'America/Anchorage'],
  ['Etc/GMT+10', 'Pacific/Honolulu'],
  ['Etc/GMT+11', 'Pacific/Midway'],
  ['Etc/GMT+12', 'Pacific/Wake'],
  ['Etc/GMT-0', 'UTC'],
  ['Etc/GMT-1', 'Europe/London'],
  ['Etc/GMT-2', 'Europe/Berlin'],
  ['Etc/GMT-3', 'Europe/Moscow'],
  ['Etc/GMT-4', 'Asia/Dubai'],
  ['Etc/GMT-5', 'Asia/Karachi'],
  ['Etc/GMT-6', 'Asia/Dhaka'],
  ['Etc/GMT-7', 'Asia/Bangkok'],
  ['Etc/GMT-8', 'Asia/Shanghai'],
  ['Etc/GMT-9', 'Asia/Tokyo'],
  ['Etc/GMT-10', 'Australia/Sydney'],
  ['Etc/GMT-11', 'Pacific/Noumea'],
  ['Etc/GMT-12', 'Pacific/Auckland'],
  ['Etc/GMT-13', 'Pacific/Tongatapu'],
  ['Etc/GMT-14', 'Pacific/Kiritimati'],
]);

export const getDeviceModel = async (defaultValue: string) =>
  systeminfo.getCapability('http://tizen.org/system/model_name') ?? defaultValue;

export const getDeviceSKU = async (defaultValue: string) => defaultValue;

export const getDeviceTimeZone = () => {
  let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timeZone?.startsWith('Etc/') || timeZone?.includes('GMT')) {
    timeZone = timeZoneMap.get(timeZone) || EASTERN_TZ;
  }

  if (!timeZone) {
    throw new Error(`${EPG_SERVICE_ERROR} Time zone not found. Falling back to ${EASTERN_TZ}`);
  }

  return timeZone;
};

export const getDeviceTTSEnabled = async () =>
  window.webapis.tvinfo.getMenuValue(TvInfoMenuKey.VOICE_GUIDE_KEY) === TvInfoMenuValue.ON;

export const parseModelNumber = (model: string) => {
  // Require valid Samsung model prefix
  if (!model || !/^[A-Z]{2}\d{2}/.test(model)) {
    return 'UNKNOWN';
  }

  // Trim model number to relevant characters for tier determination based on Samsung's model numbering scheme
  // https://developer.samsung.com/smarttv/develop/specifications/tv-model-groups.html
  return model
    .toUpperCase()
    .trim()
    .replace(/^[A-Z]{2}\d{2}/, '')
    .replace(/(FXZA|DXZA|BXZA|FXZC|FXZX|PXPA|XZA|XZX|XZS)$/, '')
    .replace(/^([A-Z]{2}\d{4})[A-Z]$/, '$1')
    .replace(/^([A-Z]+\d+)([A-Z])[A-Z]$/, '$1$2')
    .replace(/^([A-Z]+\d+[A-Z]+)[A-Z]$/, '$1');
};
